import { openImpersonationSession } from 'utils/open-impersonation-session';
import React, { FC, Fragment, useCallback, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { getImpersonateUserTokenTemplate, getUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { User, SuperAdminUserListFilter } from 'models/user';
import debounce from 'lodash.debounce';
import Loader from 'components/shared/loader/loader.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useTranslation } from 'react-i18next';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import UserList from 'components/users/users/user-list/user-list';
import UserPageActions from 'components/users/users/user-page-actions/user-page-actions';
import ImpersonateUserOrganizationsDialog from 'components/shared/impersonate-user-organizations-dialog/impersonate-user-organizations-dialog.component';
import EmailOtpDialog, { EmailOtpForUser } from 'components/shared/email-otp-dialog/email-otp-dialog.component';
import { generateEmailOtpForUserTemplate } from 'apollo/templates/user/mutation.graphql';
import { useAppSettings } from 'context/app-settings';
import AddTechniciansDialog from 'components/users/technicians/add-technicians-dialog/add-technicians-dialog.component';
import AddDentistDialog from 'components/users/dentists/add-dentist-dialog/add-dentist-dialog.component';

const Users: FC = () => {
    const [dentistDialogOpen, setDentistDialogOpen] = useState(false);
    const [technicianDialogOpen, setTechnicianDialogOpen] = useState(false);
    const [addDentistSnackbarOpen, setAddDentistSnackbarOpen] = useState(false);
    const [addTechnicianSnackbarOpen, setAddTechnicianSnackbarOpen] = useState(false);
    const [filter, setFilter] = useState<SuperAdminUserListFilter>({
        freeText: '',
        active: true,
        type: 'lab_technician',
        role: '',
        local: null,
        isWhiteLabeled: null,
    });
    const [lastOffset, setLastOffset] = useState(0);
    const isFetchingMoreRef = useRef(false);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [organizationsDialogOpen, setOrganizationsDialogOpen] = useState(false);
    const [user, setUser] = useState<User>({} as User);
    const [wlLabId, setWlLabId] = useState<string | undefined>();
    const { t: translate } = useTranslation(['users']);
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        loading: usersLoading,
        fetchMore,
        refetch,
    } = useQuery(getUserListTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        variables: {
            orgId: orgId,
            filters: filter,
            limit: 25,
            offset: 0,
        },
        onCompleted: ({ users: fetchedUsers }) => {
            if (isFetchingMoreRef.current) {
                isFetchingMoreRef.current = false;
                return;
            }
            setUsers(fetchedUsers);
            setLastOffset(fetchedUsers.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { appSettings } = useAppSettings();
    const [issuedOtp, setIssuedOtp] = useState<EmailOtpForUser | null>(null);
    const [otpError, setOtpError] = useState<string | null>(null);
    const [generateEmailOtpForUser] = useMutation(generateEmailOtpForUserTemplate, {
        onCompleted: ({ generateEmailOtpForUser }) => {
            setIssuedOtp(generateEmailOtpForUser);
        },
        onError: (error) => {
            setOtpError(
                error.message === 'USER_NOT_EMAIL_OTP_LOGIN'
                    ? translate('users:GENERATE_OTP_NOT_APPLICABLE')
                    : translate('users:GENERATE_OTP_FAILED'),
            );
        },
    });

    const handleGenerateOtp = (user: User) => {
        setOtpError(null);
        generateEmailOtpForUser({ variables: { userId: user._id } });
    };

    const [getImpersonateUserToken] = useLazyQuery(getImpersonateUserTokenTemplate, {
        fetchPolicy: 'network-only',
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ impersonateUser }) => {
            handleClose();
            openImpersonationSession(impersonateUser.token, wlLabId);
        },
    });

    const handleFetchMore = () => {
        if (isFetchingMoreRef.current) return;
        isFetchingMoreRef.current = true;
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        })
            .then(({ data }) => {
                setUsers((previousUsers) => [...previousUsers, ...data.users]);
                setLastOffset((previousOffset) => previousOffset + data.users.length);
            })
            .finally(() => {
                isFetchingMoreRef.current = false;
            });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active, type, role, local, isWhiteLabeled }: SuperAdminUserListFilter) => {
            setFilter({
                freeText: freeText.toLocaleLowerCase(),
                active: active,
                type: type,
                role: role,
                local: type === 'dentist' ? local : null,
                isWhiteLabeled: type === 'dentist' ? isWhiteLabeled : null,
            });
            setLastOffset(0);
        }, 800),
        [],
    );

    const handleSelectedOrg = (orgId: string | null) => {
        setOrgId(orgId);
    };

    const handleOpenDentistDialog = () => {
        setAddDentistSnackbarOpen(false);
        setDentistDialogOpen(true);
    };

    const handleOpenTechnicianDialog = () => {
        setAddTechnicianSnackbarOpen(false);
        setTechnicianDialogOpen(true);
    };

    const updateUser = (user: User) => {
        const allUsers = [...users];
        const index = allUsers.findIndex((userItem: User) => userItem._id === user._id);
        if (index > -1) {
            allUsers.splice(index, 1, user);
        }
        setUsers([...allUsers]);
    };

    const handleImpersonateUserLogin = (user: User) => {
        setUser(user);
        setOrganizationsDialogOpen(true);
    };

    const handleClose = () => {
        setOrganizationsDialogOpen(false);
        setUser({} as User);
    };

    const handleConfirm = async (organizationId: string, wlLabId: string | undefined) => {
        if (wlLabId) {
            setWlLabId(wlLabId);
        }
        await getImpersonateUserToken({ variables: { userId: user._id, organizationId } });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('USERS')} />
            <UserPageActions
                onOrgChange={handleSelectedOrg}
                onFilterChange={handleFilterChange}
                onAddDentistDialogOpen={handleOpenDentistDialog}
                onAddTechnicianDialogOpen={handleOpenTechnicianDialog}
            />
            {usersLoading && users.length === 0 ? (
                <Loader />
            ) : (
                <Fragment>
                    <UserList
                        filteredUsers={users}
                        onFetchMore={handleFetchMore}
                        usersLoading={usersLoading}
                        onUserUpdated={updateUser}
                        onLoginClick={handleImpersonateUserLogin}
                        onGenerateOtpClick={appSettings.emailOtpEnabled ? handleGenerateOtp : undefined}
                        refetchUsers={refetch}
                    />
                    <AddDentistDialog
                        dialogOpen={dentistDialogOpen}
                        onDialogClose={() => setDentistDialogOpen(false)}
                        onUserAdded={() => {
                            setAddDentistSnackbarOpen(true);
                            refetch();
                        }}
                        onUserLinked={() => {
                            refetch();
                        }}
                    />
                    <AddTechniciansDialog
                        isDialogOpen={technicianDialogOpen}
                        onDialogCloseClick={() => setTechnicianDialogOpen(false)}
                        onAddTechnicianClick={() => {
                            setAddTechnicianSnackbarOpen(true);
                            refetch();
                        }}
                        onUserLinked={() => {
                            refetch();
                        }}
                    />
                    {addDentistSnackbarOpen ? (
                        <AlertSnackbar type="success" message={translate('DENTIST_ADDED_MESSAGE')} />
                    ) : undefined}
                    {addTechnicianSnackbarOpen ? (
                        <AlertSnackbar type="success" message={translate('TECHNICIAN_ADDED_MESSAGE')} />
                    ) : undefined}
                    {issuedOtp ? <EmailOtpDialog otp={issuedOtp} onClose={() => setIssuedOtp(null)} /> : undefined}
                    {otpError ? <AlertSnackbar type="error" message={otpError} /> : undefined}
                    {organizationsDialogOpen ? (
                        <ImpersonateUserOrganizationsDialog
                            open={organizationsDialogOpen}
                            linkedOrganizations={user.linkedOrganizations}
                            handleClose={handleClose}
                            handleConfirm={handleConfirm}
                        />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default Users;
